import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Dialog, Transition } from '@headlessui/react';
const { Panel: DialogPanel, Title: DialogTitle } = Dialog;
const { Child: TransitionChild } = Transition;

import { XMarkIcon, ExclamationTriangleIcon } from '@heroicons/react/24/outline';
import { Button } from '@/components/ui/Button';
import { clsx } from 'clsx';
import { useCreateCustodian, useUpdateCustodian } from '@/hooks/custodian';
import { getErrorMessage } from '@/utils/get-error-message';
import { CustodianItem } from '@/types';

interface AddCustodianModalProps {
  isOpen: boolean;
  onClose: () => void;
  // edit mode prefills from `custodian` and submits a PATCH; defaults to create.
  mode?: 'create' | 'edit';
  custodian?: CustodianItem;
}

const INITIAL_FORM_DATA = {
  name: '',
  shortCode: '',
  accountNumber: '',
  isActive: true,
};

const buildEditFormData = (c: CustodianItem): typeof INITIAL_FORM_DATA => ({
  name: c.name ?? '',
  shortCode: c.short_code ?? '',
  accountNumber: c.account_number ?? '',
  isActive: c.is_active,
});

export const AddCustodianModal: React.FC<AddCustodianModalProps> = ({ isOpen, onClose, mode = 'create', custodian }) => {
  const isEditMode = mode === 'edit';
  const createCustodian = useCreateCustodian();
  const updateCustodian = useUpdateCustodian();
  // The mutation backing the current mode — drives the submit button's loading
  // state and the inline save-error banner (shared by create and edit).
  const activeMutation = isEditMode ? updateCustodian : createCustodian;

  // Baseline the form was seeded with — stable for the modal's lifetime since it
  // unmounts/remounts per open (conditionally rendered by the parent).
  const baseline = useMemo(
    () => (isEditMode && custodian ? buildEditFormData(custodian) : INITIAL_FORM_DATA),
    [isEditMode, custodian],
  );

  const [formData, setFormData] = useState(baseline);
  const [showCloseConfirmation, setShowCloseConfirmation] = useState(false);
  const [attemptedSubmit, setAttemptedSubmit] = useState(false);

  // Derived (not flagged on every keystroke) so reverting a field back to its
  // original value clears the dirty state — no spurious "Discard changes?".
  const hasFormChanges =
    formData.name !== baseline.name ||
    formData.shortCode !== baseline.shortCode ||
    formData.accountNumber !== baseline.accountNumber ||
    formData.isActive !== baseline.isActive;

  const handleCloseWithoutConfirmation = useCallback(() => {
    onClose();
    setFormData(INITIAL_FORM_DATA);
    setShowCloseConfirmation(false);
    setAttemptedSubmit(false);
  }, [onClose]);

  const handleClose = useCallback(() => {
    if (hasFormChanges) {
      setShowCloseConfirmation(true);
    } else {
      handleCloseWithoutConfirmation();
    }
  }, [hasFormChanges, handleCloseWithoutConfirmation]);

  useEffect(() => {
    const handleEscapeKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && isOpen && !showCloseConfirmation) {
        event.preventDefault();
        event.stopPropagation();
        handleClose();
      }
    };
    if (isOpen) {
      document.addEventListener('keydown', handleEscapeKey, true);
    }
    return () => {
      document.removeEventListener('keydown', handleEscapeKey, true);
    };
  }, [isOpen, handleClose, showCloseConfirmation]);

  const handleFormDataChange = (updates: Partial<typeof INITIAL_FORM_DATA>) => {
    setFormData((prev) => ({ ...prev, ...updates }));
  };

  const nameInvalid = !formData.name.trim();
  const shortCodeInvalid = !formData.shortCode.trim();

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setAttemptedSubmit(true);
    // Required-field guard. short_code is immutable in edit mode, so only the
    // name is gated there; create requires both.
    if (nameInvalid || (!isEditMode && shortCodeInvalid)) {
      return;
    }
    // LR-468: a successful write always closes the modal. A rejected write
    // (e.g. HTTP 400 when account_number is unknown to Backoffice) keeps the
    // modal open and surfaces the field error inline via getErrorMessage.
    const onSuccess = () => handleCloseWithoutConfirmation();
    if (isEditMode && custodian) {
      // LR-468: send account_number only when it actually changed, so editing
      // just name / is_active never re-triggers the blocking Backoffice lookup
      // (a custodian whose base account has since gone 404 in BO stays editable).
      updateCustodian.mutate(
        {
          id: custodian.id,
          data: formData,
          accountNumberChanged: formData.accountNumber !== baseline.accountNumber,
        },
        { onSuccess },
      );
    } else {
      createCustodian.mutate(formData, { onSuccess });
    }
  };

  const labelClassName = 'block text-xs text-gray-500 mb-1';
  const inputClassName = 'w-full px-2 py-1 text-sm border border-gray-300 rounded focus:ring-blue-500 focus:border-blue-500';

  return (
    <>
      <Transition show={isOpen} as={React.Fragment}>
        <Dialog as="div" className="relative z-[100]" onClose={handleClose}>
          <TransitionChild
            as={React.Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-black bg-opacity-25 transition-opacity" />
          </TransitionChild>

          <div className="fixed inset-0 z-[101] overflow-y-auto">
            <div className="flex min-h-full items-start justify-center p-4 py-10 text-center sm:items-start sm:p-0">
              <TransitionChild
                as={React.Fragment}
                enter="ease-out duration-300"
                enterFrom="opacity-0 scale-95"
                enterTo="opacity-100 scale-100"
                leave="ease-in duration-200"
                leaveFrom="opacity-100 scale-100"
                leaveTo="opacity-0 scale-95"
              >
                <DialogPanel className="relative transform overflow-hidden rounded-lg bg-white text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-lg flex flex-col">
                  <div className="flex items-center justify-between border-b border-gray-200 px-6 py-4">
                    <DialogTitle as="h3" className="text-lg font-semibold text-gray-900">
                      {isEditMode ? 'Edit Custodian' : 'New Custodian'}
                    </DialogTitle>
                    <button onClick={handleClose} className="text-gray-400 hover:text-gray-500 focus:outline-none">
                      <XMarkIcon className="h-6 w-6" />
                    </button>
                  </div>

                  <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto">
                    <div className="p-6 grid grid-cols-1 gap-4">
                      <InputField
                        label="Custodian Name"
                        required
                        value={formData.name}
                        onChange={(v) => handleFormDataChange({ name: v })}
                        labelClassName={labelClassName}
                        inputClassName={inputClassName}
                        placeholder="e.g. JPMorgan"
                        error={attemptedSubmit && nameInvalid ? 'Name is required' : undefined}
                      />
                      <InputField
                        label="Short Code"
                        required
                        value={formData.shortCode}
                        disabled={isEditMode}
                        onChange={(v) => handleFormDataChange({ shortCode: v })}
                        labelClassName={labelClassName}
                        inputClassName={inputClassName}
                        placeholder="e.g. JPM"
                        error={attemptedSubmit && !isEditMode && shortCodeInvalid ? 'Short code is required' : undefined}
                      />
                      <InputField
                        label="Account Number"
                        value={formData.accountNumber}
                        onChange={(v) => handleFormDataChange({ accountNumber: v })}
                        labelClassName={labelClassName}
                        inputClassName={inputClassName}
                        placeholder="e.g. US001"
                        helper="Base account only — the .NNN settlement suffix is derived automatically."
                      />
                      {/* Active flag — editable only in edit mode (new custodians are active). */}
                      {isEditMode && (
                        <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer pt-1">
                          <input
                            type="checkbox"
                            checked={formData.isActive}
                            onChange={(e) => handleFormDataChange({ isActive: e.target.checked })}
                            className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                          />
                          <span className="text-sm font-medium text-gray-700">Active</span>
                        </label>
                      )}
                    </div>

                    {activeMutation.isError && (
                      <div
                        role="alert"
                        className="mx-6 mb-4 flex items-start gap-2 rounded border border-red-200 bg-red-50 px-3 py-2"
                      >
                        <ExclamationTriangleIcon className="h-4 w-4 text-red-600 mt-0.5 shrink-0" aria-hidden />
                        <p className="text-xs text-red-800">
                          {getErrorMessage(activeMutation.error, 'Failed to save. Please try again.')}
                        </p>
                      </div>
                    )}

                    <div className="flex items-center justify-end gap-3 border-t border-gray-200 px-6 py-4 bg-white sticky bottom-0">
                      <Button type="button" variant="secondary" onClick={handleClose}>
                        Cancel
                      </Button>
                      <Button type="submit" variant="primary" isLoading={activeMutation.isPending}>
                        {isEditMode ? 'Save Changes' : 'Create Custodian'}
                      </Button>
                    </div>
                  </form>
                </DialogPanel>
              </TransitionChild>
            </div>
          </div>
        </Dialog>
      </Transition>

      <Transition show={showCloseConfirmation} as={React.Fragment}>
        <Dialog as="div" className="relative z-[110]" onClose={() => setShowCloseConfirmation(false)}>
          <TransitionChild
            as={React.Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-black bg-opacity-25" />
          </TransitionChild>
          <div className="fixed inset-0 overflow-y-auto">
            <div className="flex min-h-full items-center justify-center p-4">
              <TransitionChild
                as={React.Fragment}
                enter="ease-out duration-300"
                enterFrom="opacity-0 scale-95"
                enterTo="opacity-100 scale-100"
                leave="ease-in duration-200"
                leaveFrom="opacity-100 scale-100"
                leaveTo="opacity-0 scale-95"
              >
                <DialogPanel className="w-full max-w-md transform overflow-hidden rounded-lg bg-white shadow-xl transition-all">
                  <div className="p-6">
                    <DialogTitle className="text-lg font-semibold text-gray-900 mb-2">
                      Discard Changes?
                    </DialogTitle>
                    <p className="text-sm text-gray-600 mb-6">
                      You have unsaved changes. Are you sure you want to close without saving?
                    </p>
                    <div className="flex gap-3 justify-end">
                      <button
                        onClick={() => setShowCloseConfirmation(false)}
                        className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500"
                      >
                        Continue Editing
                      </button>
                      <button
                        onClick={() => {
                          setShowCloseConfirmation(false);
                          handleCloseWithoutConfirmation();
                        }}
                        className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-md hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500"
                      >
                        Discard Changes
                      </button>
                    </div>
                  </div>
                </DialogPanel>
              </TransitionChild>
            </div>
          </div>
        </Dialog>
      </Transition>
    </>
  );
};

interface InputFieldProps {
  label: string;
  value: string;
  onChange: (v: string) => void;
  type?: string;
  disabled?: boolean;
  required?: boolean;
  labelClassName?: string;
  inputClassName?: string;
  placeholder?: string;
  helper?: string;
  error?: string;
}

const InputField = ({ label, value, onChange, type = 'text', disabled = false, required = false, labelClassName, inputClassName, placeholder, helper, error }: InputFieldProps) => (
  <div>
    <label className={labelClassName}>
      {label} {required && <span className="text-red-500">*</span>}
    </label>
    <input
      type={type}
      value={value}
      disabled={disabled}
      placeholder={placeholder}
      onChange={(e) => onChange(e.target.value)}
      className={clsx(inputClassName, disabled && 'bg-gray-50 text-gray-400')}
    />
    {error ? (
      <p className="mt-1 text-xs text-red-600">{error}</p>
    ) : helper ? (
      <p className="mt-1 text-xs text-gray-400">{helper}</p>
    ) : null}
  </div>
);
